import { useEffect, useState } from 'react';
import {
  Activity,
  Check,
  ClipboardList,
  Copy,
  Eye,
  EyeOff,
  LayoutDashboard,
  Megaphone,
  Monitor,
  Pencil,
  Plus,
  Printer,
  QrCode,
  RefreshCw,
  Settings,
  Shuffle,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import QRCode from 'qrcode';
import { api, apiBlob } from '../api';
import { Brand, Button, Card, Empty, GameIcon, Notice, PixelIcon, Shell } from '../components';
import { awardIcons, boostIcon, powerupIcons, taskTypeIcons } from '../pixelIcons';
import {
  awardSteps,
  awardStepMeta,
  DEFAULT_DARE,
  DEFAULT_SUCCESS_MESSAGE,
  POWERUPS_ENABLED,
  powerupKinds,
  powerupMeta,
  icons,
  teamAccentColor,
  teamColorName,
  teamColors,
  randomHalloweenTeamName,
  taskStatuses,
  taskStatusMeta,
  taskTypes,
  taskTypeMeta,
} from '../../shared/constants';
import type {
  AwardStep,
  PowerupKind,
  TaskStatus,
  TaskType,
  TriviaAnswerMode,
} from '../../shared/constants';
import { releaseLabel, releaseTime } from '../../shared/schedule';
import { loadDeck, referenceImageUrl } from '../promptDeck';

type Tab = 'dashboard' | 'teams' | 'tasks' | 'submissions' | 'games';
interface Game {
  id: string;
  name: string;
  joinCode: string;
  status: string;
  leaderboardVisible: boolean;
  registrationOpen: boolean;
  soloMode: boolean;
  startedAt?: string | null;
  dealStart?: number;
  dealSize?: number;
  dealEveryMinutes?: number;
}
interface GameInput {
  name: string;
  joinCode: string;
  soloMode?: boolean;
}
/** When a task appears for players. */
type Release = 'start' | 'scheduled' | 'hidden';
interface Snapshot {
  game: Game;
  players: any[];
  teams: any[];
  tasks: any[];
  submissions: any[];
  activity: any[];
  scores: any[];
  announcements: any[];
  displayState: { mode: string; awardStep: string | null; referenceId: string | null };
}

function randomJoinCode() {
  return `RAVE${Math.floor(10 + Math.random() * 89)}`;
}

function teamStyle(color: string): React.CSSProperties {
  return {
    '--team-color': color,
    '--team-accent': teamAccentColor(color),
  } as React.CSSProperties;
}

export function AdminPage() {
  const [games, setGames] = useState<Game[]>([]);
  const [gameId, setGameId] = useState('');
  const [data, setData] = useState<Snapshot | null>(null);
  const [tab, setTab] = useState<Tab>('dashboard');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [qrModal, setQrModal] = useState<{
    dataUrl: string;
    url: string;
    title: string;
    eyebrow: string;
    detail: string;
  } | null>(null);
  async function loadGames(preferredId?: string) {
    try {
      const result = await api<{ games: Game[] }>('/api/admin/games');
      setGames(result.games);
      const nextId =
        preferredId && result.games.some((g) => g.id === preferredId)
          ? preferredId
          : gameId && result.games.some((g) => g.id === gameId)
            ? gameId
            : (result.games[0]?.id ?? '');
      if (nextId !== gameId) setGameId(nextId);
      if (!result.games.length) {
        setData(null);
        setTab('games');
      }
      return result.games;
    } catch (err) {
      setError((err as Error).message);
      return [] as Game[];
    } finally {
      setReady(true);
    }
  }
  async function load() {
    if (!gameId) {
      setData(null);
      return;
    }
    try {
      setData(await api(`/api/admin/games/${gameId}`));
    } catch (err) {
      setError((err as Error).message);
    }
  }
  useEffect(() => {
    void loadGames();
  }, []);
  useEffect(() => {
    void load();
    if (!gameId) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [gameId]);
  useEffect(() => {
    if (!qrModal) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setQrModal(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [qrModal]);
  async function act(path: string, body?: unknown, method = 'POST') {
    setBusy(true);
    setError('');
    try {
      await api(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
      await Promise.all([load(), loadGames()]);
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function createGame(input: GameInput) {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ id: string }>('/api/admin/games', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      await loadGames(result.id);
      setGameId(result.id);
      setTab('dashboard');
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setBusy(false);
    }
  }
  async function updateGame(id: string, input: GameInput) {
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/games/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
      await Promise.all([loadGames(gameId), gameId === id ? load() : Promise.resolve()]);
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setBusy(false);
    }
  }
  async function cloneGame(sourceId: string, input: GameInput) {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ id: string }>(`/api/admin/games/${sourceId}/clone`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      await loadGames(result.id);
      setGameId(result.id);
      setTab('dashboard');
    } catch (err) {
      setError((err as Error).message);
      throw err;
    } finally {
      setBusy(false);
    }
  }
  async function deleteGame(id: string) {
    setBusy(true);
    setError('');
    try {
      await api(`/api/admin/games/${id}`, { method: 'DELETE' });
      const remaining = await loadGames(id === gameId ? undefined : gameId);
      if (id === gameId) {
        const next = remaining[0]?.id ?? '';
        setGameId(next);
        if (!next) setData(null);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function showJoinQr() {
    if (!data) return;
    const url = `${location.origin}/join/${data.game.joinCode}`;
    const dataUrl = await QRCode.toDataURL(url, {
      width: 800,
      margin: 3,
      color: { dark: '#120b1c', light: '#ffffff' },
    });
    setQrModal({
      dataUrl,
      url,
      title: data.game.name,
      eyebrow: 'Scan to join',
      detail: data.game.joinCode,
    });
  }

  async function showTaskQr(task: { name: string; type: string; qrToken?: string | null }) {
    if (!task.qrToken) return;
    const url = `${location.origin}/scan/${task.qrToken}`;
    const dataUrl = await QRCode.toDataURL(url, {
      width: 800,
      margin: 3,
      color: { dark: '#120b1c', light: '#ffffff' },
    });
    const type = taskTypes.includes(task.type as TaskType) ? (task.type as TaskType) : 'qr';
    setQrModal({
      dataUrl,
      url,
      title: task.name,
      eyebrow: 'Scan to complete',
      detail: taskTypeMeta[type].title,
    });
  }
  if (!ready)
    return (
      <Shell>
        <div className="center">
          <RefreshCw className="spin" />
          {error && <Notice type="error">{error}</Notice>}
        </div>
      </Shell>
    );
  if (!data && tab !== 'games')
    return (
      <Shell>
        <div className="center">
          <RefreshCw className="spin" />
          {error && <Notice type="error">{error}</Notice>}
        </div>
      </Shell>
    );
  const pending = data?.submissions.filter((s) => s.status === 'pending').length ?? 0;
  const mainTabs = [
    ['dashboard', LayoutDashboard, 'Dashboard'],
    ['teams', Users, data?.game.soloMode ? 'Players' : 'Teams & Players'],
    ['tasks', ClipboardList, 'Tasks'],
    ['submissions', Check, 'Submissions'],
  ] as const;
  return (
    <Shell className="admin-shell">
      <aside className="sidebar">
        <Brand compact />
        {games.length > 0 && (
          <div className="admin-game-picker">
            <select
              aria-label="Select game"
              value={gameId}
              onChange={(e) => {
                setGameId(e.target.value);
                if (tab === 'games') setTab('dashboard');
              }}
            >
              {games.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <nav className="sidebar-nav">
          {mainTabs.map(([key, Icon, label]) => (
            <button
              key={key}
              className={tab === key ? 'active' : ''}
              disabled={!data}
              onClick={() => setTab(key)}
            >
              <Icon size={18} />
              {label}
              {key === 'submissions' && pending > 0 ? <b>{pending}</b> : null}
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {data && (
            <>
              <a
                className="sidebar-join-qr"
                href={`/display/${data.game.joinCode}`}
                target="_blank"
                rel="noreferrer"
              >
                <Monitor size={18} />
                Party Display
              </a>
              <button type="button" className="sidebar-join-qr" onClick={() => void showJoinQr()}>
                <QrCode size={18} />
                Join QR
              </button>
            </>
          )}
          <nav className="sidebar-nav-footer">
            <button className={tab === 'games' ? 'active' : ''} onClick={() => setTab('games')}>
              <Settings size={18} />
              Game Management
            </button>
          </nav>
        </div>
      </aside>
      <div className="admin-main">
        {error && (
          <Notice type="error" onClose={() => setError('')}>
            {error}
          </Notice>
        )}
        {tab === 'games' && (
          <GameManagement
            games={games}
            busy={busy}
            onCreate={createGame}
            onUpdate={updateGame}
            onClone={cloneGame}
            onDelete={deleteGame}
          />
        )}
        {tab === 'dashboard' && data && <Dashboard data={data} busy={busy} act={act} />}
        {tab === 'teams' &&
          data &&
          (data.game.soloMode ? (
            <SoloPlayers data={data} />
          ) : (
            <TeamsAndPlayers data={data} busy={busy} act={act} />
          ))}
        {tab === 'tasks' && data && (
          <Tasks data={data} busy={busy} act={act} onShowQr={showTaskQr} />
        )}
        {tab === 'submissions' && data && <Submissions data={data} busy={busy} act={act} />}
      </div>
      {qrModal && (
        <div
          className="join-qr-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="QR code"
          onClick={() => setQrModal(null)}
        >
          <div className="join-qr-panel" onClick={(e) => e.stopPropagation()}>
            <div className="join-qr-toolbar no-print">
              <Button variant="secondary" onClick={() => window.print()}>
                <Printer size={16} />
                Print
              </Button>
              <Button variant="ghost" onClick={() => setQrModal(null)} aria-label="Close">
                <X size={16} />
                Close
              </Button>
            </div>
            <div className="join-qr-print">
              <Brand compact />
              <h2>{qrModal.title}</h2>
              <p>{qrModal.eyebrow}</p>
              <img src={qrModal.dataUrl} alt={`QR code for ${qrModal.title}`} />
              <strong>{qrModal.detail}</strong>
              <small>{qrModal.url}</small>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

const SOLO_HINT =
  'Every player gets their own team when they join, so scores and awards are per person.';

function GameManagement({
  games,
  busy,
  onCreate,
  onUpdate,
  onClone,
  onDelete,
}: {
  games: Game[];
  busy: boolean;
  onCreate: (input: GameInput) => Promise<void>;
  onUpdate: (id: string, input: GameInput) => Promise<void>;
  onClone: (sourceId: string, input: GameInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [createForm, setCreateForm] = useState({
    name: 'Rave Cave',
    joinCode: randomJoinCode(),
    soloMode: false,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', joinCode: '', soloMode: false });
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [cloneForm, setCloneForm] = useState({ name: '', joinCode: '' });

  function startEdit(game: Game) {
    setCloningId(null);
    setEditingId(game.id);
    setEditForm({ name: game.name, joinCode: game.joinCode, soloMode: game.soloMode });
  }
  function startClone(game: Game) {
    setEditingId(null);
    setCloningId(game.id);
    setCloneForm({
      name: `${game.name} Copy`,
      joinCode: randomJoinCode(),
    });
  }

  return (
    <div className="game-management">
      <div className="section-title">
        <div>
          <h2>Game Management</h2>
          <p>Create, edit, clone, and archive games.</p>
        </div>
      </div>
      <Card>
        <h2>Create Game</h2>
        <div className="form-grid">
          <label>
            Name
            <input
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            />
          </label>
          <label>
            Join code
            <input
              value={createForm.joinCode}
              onChange={(e) =>
                setCreateForm({ ...createForm, joinCode: e.target.value.toUpperCase() })
              }
            />
          </label>
          <label className="span-2 toggle">
            <input
              type="checkbox"
              checked={createForm.soloMode}
              onChange={(e) => setCreateForm({ ...createForm, soloMode: e.target.checked })}
            />
            Solo players (no teams)
          </label>
          <p className="span-2 field-hint">{SOLO_HINT}</p>
        </div>
        <Button
          busy={busy}
          onClick={() =>
            void onCreate(createForm)
              .then(() =>
                setCreateForm({ name: 'Rave Cave', joinCode: randomJoinCode(), soloMode: false }),
              )
              .catch(() => undefined)
          }
        >
          <Plus size={16} />
          Create Game
        </Button>
      </Card>

      <Card>
        <h2>Existing Games</h2>
        {games.length === 0 ? (
          <Empty>No games yet. Create one above.</Empty>
        ) : (
          <div className="game-admin-list">
            {games.map((game) => (
              <div key={game.id} className="game-admin-row">
                {editingId === game.id ? (
                  <div className="game-admin-edit">
                    <div className="form-grid">
                      <label>
                        Name
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        />
                      </label>
                      <label>
                        Join code
                        <input
                          value={editForm.joinCode}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              joinCode: e.target.value.toUpperCase(),
                            })
                          }
                        />
                      </label>
                      <label className="span-2 toggle">
                        <input
                          type="checkbox"
                          checked={editForm.soloMode}
                          disabled={game.soloMode}
                          onChange={(e) => setEditForm({ ...editForm, soloMode: e.target.checked })}
                        />
                        Solo players (no teams)
                      </label>
                      <p className="span-2 field-hint">
                        {game.soloMode
                          ? 'Solo mode stays on once players have their own teams.'
                          : `${SOLO_HINT} Players who already joined without a team get one now.`}
                      </p>
                    </div>
                    <div className="button-wrap">
                      <Button
                        busy={busy}
                        onClick={() =>
                          void onUpdate(game.id, editForm)
                            .then(() => setEditingId(null))
                            .catch(() => undefined)
                        }
                      >
                        Save
                      </Button>
                      <Button variant="ghost" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : cloningId === game.id ? (
                  <div className="game-admin-edit">
                    <div className="form-grid">
                      <label>
                        Cloned name
                        <input
                          value={cloneForm.name}
                          onChange={(e) => setCloneForm({ ...cloneForm, name: e.target.value })}
                        />
                      </label>
                      <label>
                        Join code
                        <input
                          value={cloneForm.joinCode}
                          onChange={(e) =>
                            setCloneForm({
                              ...cloneForm,
                              joinCode: e.target.value.toUpperCase(),
                            })
                          }
                        />
                      </label>
                    </div>
                    <div className="button-wrap">
                      <Button
                        busy={busy}
                        onClick={() =>
                          void onClone(game.id, cloneForm)
                            .then(() => setCloningId(null))
                            .catch(() => undefined)
                        }
                      >
                        <Copy size={16} />
                        Clone
                      </Button>
                      <Button variant="ghost" onClick={() => setCloningId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h3>{game.name}</h3>
                      <p>
                        Code <strong>{game.joinCode}</strong> · {game.status}
                        {game.soloMode ? ' · solo' : ''}
                      </p>
                    </div>
                    <div className="game-admin-actions">
                      <Button variant="ghost" onClick={() => startEdit(game)}>
                        <Pencil size={16} />
                        Edit
                      </Button>
                      <Button variant="ghost" onClick={() => startClone(game)}>
                        <Copy size={16} />
                        Clone
                      </Button>
                      <Button
                        variant="danger"
                        busy={busy}
                        onClick={() => {
                          if (
                            confirm(
                              `Archive “${game.name}”? It will be hidden from join and admin lists.`,
                            )
                          )
                            void onDelete(game.id);
                        }}
                      >
                        <Trash2 size={16} />
                        Delete
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Dashboard({ data, busy, act }: { data: Snapshot; busy: boolean; act: any }) {
  const [announceOpen, setAnnounceOpen] = useState(false);
  const stats = [
    ['Players', data.players.length],
    ['Teams', data.teams.length],
    ['Active Tasks', data.tasks.filter((t) => t.visibility === 'start').length],
    ['Pending', data.submissions.filter((s) => s.status === 'pending').length],
  ];
  const status = data.game.status;
  const lifecycleSteps = [
    {
      id: 'setup',
      label: 'Setup',
      description: data.game.soloMode
        ? 'Add tasks, set their timers, and share the join QR'
        : 'Configure teams, tasks, and settings',
    },
    {
      id: 'live',
      label: 'Live',
      description: 'Challenges are open for submissions',
    },
    {
      id: 'ended',
      label: 'Ended',
      description: 'Play stops and scores settle',
    },
    {
      id: 'awards',
      label: 'Awards',
      description: 'Celebrate winners and wrap up',
    },
  ] as const;
  const stepOrder = lifecycleSteps.map((step) => step.id);
  const activeStepId = status === 'paused' ? 'live' : status === 'registration' ? 'setup' : status;
  const activeStepIndex = stepOrder.indexOf(activeStepId as (typeof stepOrder)[number]);
  return (
    <>
      <div className="section-title">
        <div>
          <h2>Dashboard</h2>
          <p>Game status, controls, and live activity.</p>
        </div>
        <div className="section-title-actions">
          <a
            className="button button--secondary"
            href={`/display/${data.game.joinCode}`}
            target="_blank"
            rel="noreferrer"
          >
            <Monitor size={18} />
            Party Display
          </a>
          <Button onClick={() => setAnnounceOpen(true)}>
            <Megaphone size={18} />
            Send Announcement
          </Button>
        </div>
      </div>
      <div className="stat-grid">
        {stats.map(([label, value]) => (
          <Card key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </Card>
        ))}
      </div>
      <div className="dashboard-grid">
        <Card>
          <h2>Game Controls</h2>
          <div className="lifecycle">
            <ol className="lifecycle-steps">
              {lifecycleSteps.map((step, index) => {
                const isCurrent = activeStepId === step.id;
                const isComplete = activeStepIndex > index;
                const showPaused = isCurrent && status === 'paused';
                return (
                  <li
                    key={step.id}
                    className={[
                      'lifecycle-step',
                      isCurrent ? 'lifecycle-step--current' : '',
                      isComplete ? 'lifecycle-step--complete' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <button
                      type="button"
                      disabled={busy || isCurrent}
                      onClick={() =>
                        act(`/api/admin/games/${data.game.id}/lifecycle`, { action: step.id })
                      }
                    >
                      <span className="lifecycle-step-index">{index + 1}</span>
                      <span className="lifecycle-step-copy">
                        <strong>{showPaused ? 'Paused' : step.label}</strong>
                        <small>{showPaused ? 'Submissions are on hold' : step.description}</small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {(status === 'live' || status === 'paused') && (
              <div className="lifecycle-pause">
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={() =>
                    act(`/api/admin/games/${data.game.id}/lifecycle`, {
                      action: status === 'live' ? 'paused' : 'live',
                    })
                  }
                >
                  {status === 'live' ? 'Pause game' : 'Resume game'}
                </Button>
              </div>
            )}
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={data.game.registrationOpen}
              onChange={(e) =>
                act(
                  `/api/admin/games/${data.game.id}`,
                  { registrationOpen: e.target.checked },
                  'PATCH',
                )
              }
            />
            Registration open
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={data.game.leaderboardVisible}
              onChange={(e) =>
                act(
                  `/api/admin/games/${data.game.id}`,
                  { leaderboardVisible: e.target.checked },
                  'PATCH',
                )
              }
            />
            Show scores on the party display
          </label>
          <span className="field-hint">
            Scores stay hidden from the display until the awards unless this is on. Players only see
            their own team&rsquo;s score.
          </span>
        </Card>
        <StandingsCard teams={data.teams} />
        {status === 'awards' && <AwardsPanel data={data} busy={busy} act={act} />}
        <Card>
          <h2>Recent Activity</h2>
          <div className="activity-list">
            {data.activity.length ? (
              data.activity.slice(0, 10).map((item) => (
                <div key={item.id}>
                  <Activity size={15} />
                  <span>{item.summary}</span>
                  <time>{relative(item.createdAt)}</time>
                </div>
              ))
            ) : (
              <Empty>No activity yet.</Empty>
            )}
          </div>
        </Card>
      </div>
      {announceOpen && (
        <AnnouncementModal
          data={data}
          busy={busy}
          act={act}
          onClose={() => setAnnounceOpen(false)}
        />
      )}
    </>
  );
}

/** Admin-only standings, collapsed by default so a projected admin screen does not spoil them. */
function StandingsCard({
  teams,
}: {
  teams: { id: string; name: string; icon: string; score: number }[];
}) {
  const [revealed, setRevealed] = useState(false);
  const ranked = [...teams].sort((a, b) => Number(b.score) - Number(a.score));
  return (
    <Card className="standings-card">
      <div className="section-title">
        <div>
          <h2>Standings</h2>
          <p>Only admins can see this.</p>
        </div>
        <Button variant="secondary" onClick={() => setRevealed((value) => !value)}>
          {revealed ? <EyeOff size={18} /> : <Eye size={18} />}
          {revealed ? 'Hide standings' : 'Reveal standings'}
        </Button>
      </div>
      {revealed ? (
        ranked.length ? (
          <ol className="standings-list">
            {ranked.map((team) => (
              <li key={team.id}>
                <GameIcon name={team.icon} />
                <span>{team.name}</span>
                <strong>{Number(team.score) || 0}</strong>
              </li>
            ))}
          </ol>
        ) : (
          <Empty>No teams yet.</Empty>
        )
      ) : null}
    </Card>
  );
}

function AwardsPanel({ data, busy, act }: { data: Snapshot; busy: boolean; act: any }) {
  const current = data.displayState;
  return (
    <Card>
      <h2>Awards</h2>
      <p>Reveal each award on the party display, one at a time.</p>
      <div className="awards-steps">
        {awardSteps
          .filter((step) => !(data.game.soloMode && step === 'individual'))
          .map((step: AwardStep) => {
            const meta = awardStepMeta[step];
            const isCurrent = current.mode === 'award' && current.awardStep === step;
            return (
              <Button
                key={step}
                variant={isCurrent ? 'success' : 'secondary'}
                busy={busy}
                onClick={() =>
                  act(`/api/admin/games/${data.game.id}/display`, {
                    mode: 'award',
                    awardStep: step,
                  })
                }
              >
                <span aria-hidden>
                  <PixelIcon icon={awardIcons[step].icon} tint={awardIcons[step].tint} />
                </span>
                Reveal {meta.title}
              </Button>
            );
          })}
      </div>
      {current.mode === 'award' && (
        <Button
          variant="ghost"
          busy={busy}
          onClick={() => act(`/api/admin/games/${data.game.id}/display`, { mode: 'leaderboard' })}
        >
          Back to final leaderboard
        </Button>
      )}
    </Card>
  );
}

/** Solo games have no teams to manage: each player is their own team. */
function SoloPlayers({ data }: { data: Snapshot }) {
  const scoreFor = (teamId: string | null) =>
    data.teams.find((team) => team.id === teamId)?.score ?? 0;
  return (
    <div className="roster-page">
      <section className="roster-panel">
        <div className="section-title">
          <div>
            <h2>Players</h2>
            <p>Solo game: everyone plays for themselves and gets their own team on joining.</p>
          </div>
        </div>
        <div className="team-list">
          {data.players.length ? (
            data.players.map((player) => (
              <Card key={player.id} className="team-row">
                <span className="team-icon">
                  <GameIcon name={player.avatar} />
                </span>
                <div>
                  <h3>{player.nickname}</h3>
                  <small className="team-members-empty">Joined {relative(player.createdAt)}</small>
                </div>
                <strong>{scoreFor(player.teamId)} pts</strong>
              </Card>
            ))
          ) : (
            <Empty>No players yet. Share the join QR to get people in.</Empty>
          )}
        </div>
      </section>
    </div>
  );
}

function TeamsAndPlayers({ data, busy, act }: { data: Snapshot; busy: boolean; act: any }) {
  const teamIcons: readonly string[] = icons;
  const [teamModal, setTeamModal] = useState<
    null | { mode: 'create' } | { mode: 'edit'; id: string }
  >(null);
  const [form, setForm] = useState({
    name: '',
    icon: teamIcons[data.teams.length % teamIcons.length],
    color: teamColors[data.teams.length % teamColors.length].value,
  });
  const unassigned = data.players.filter((p) => !p.teamId);
  const isEditing = teamModal?.mode === 'edit';

  useEffect(() => {
    if (!teamModal) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setTeamModal(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [teamModal]);

  function openCreate() {
    setForm({
      name: '',
      icon: teamIcons[data.teams.length % teamIcons.length],
      color: teamColors[data.teams.length % teamColors.length].value,
    });
    setTeamModal({ mode: 'create' });
  }

  function openEdit(team: { id: string; name: string; icon: string; color: string }) {
    setForm({
      name: team.name,
      icon: teamIcons.includes(team.icon) ? team.icon : teamIcons[0],
      color: team.color,
    });
    setTeamModal({ mode: 'edit', id: team.id });
  }

  function randomizeForm() {
    setForm({
      name: randomHalloweenTeamName(),
      icon: teamIcons[Math.floor(Math.random() * teamIcons.length)],
      color: teamColors[Math.floor(Math.random() * teamColors.length)].value,
    });
  }

  async function saveTeam() {
    if (!teamModal || !form.name.trim()) return;
    const payload = {
      name: form.name.trim(),
      icon: form.icon,
      color: form.color,
    };
    if (teamModal.mode === 'create') {
      const ok = await act(`/api/admin/games/${data.game.id}/teams`, payload);
      if (ok) setTeamModal(null);
      return;
    }
    const ok = await act(`/api/admin/teams/${teamModal.id}`, payload, 'PATCH');
    if (ok) setTeamModal(null);
  }

  return (
    <>
      <div className="roster-page">
        <section className="roster-panel">
          <div className="section-title">
            <div>
              <h2>Teams</h2>
              <p>Create teams, then assign players</p>
            </div>
            <Button onClick={openCreate}>
              <Plus size={18} />
              New team
            </Button>
          </div>

          <div className="team-list">
            {data.teams.length ? (
              data.teams.map((team) => {
                const members = data.players.filter((p) => p.teamId === team.id);
                return (
                  <Card key={team.id} className="team-row" style={teamStyle(team.color)}>
                    <span className="team-icon">
                      <GameIcon name={team.icon} />
                    </span>
                    <div>
                      <h3>{team.name}</h3>
                      {members.length ? (
                        <ul className="team-members">
                          {members.map((player) => (
                            <li key={player.id}>
                              <span aria-hidden>
                                <GameIcon name={player.avatar} />
                              </span>
                              {player.nickname}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <small className="team-members-empty">No players assigned</small>
                      )}
                    </div>
                    <div className="team-actions">
                      <Button variant="ghost" onClick={() => openEdit(team)}>
                        <Pencil size={16} />
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        busy={busy}
                        onClick={() => {
                          if (
                            confirm(
                              `Delete “${team.name}”? Players on this team will become unassigned.`,
                            )
                          )
                            void act(`/api/admin/teams/${team.id}`, undefined, 'DELETE');
                        }}
                      >
                        <Trash2 size={16} />
                        Delete
                      </Button>
                    </div>
                  </Card>
                );
              })
            ) : (
              <Empty>No teams yet. Create one with New team.</Empty>
            )}
          </div>
        </section>

        <section className="roster-panel">
          <div className="section-title">
            <div>
              <h2>Players</h2>
              <p>
                {unassigned.length
                  ? `${unassigned.length} unassigned · pick a team for each player`
                  : 'Assign or reassign players to teams'}
              </p>
            </div>
            <Button
              busy={busy}
              disabled={!data.teams.length || !data.players.length}
              onClick={() => act(`/api/admin/games/${data.game.id}/teams/randomize`)}
            >
              <Shuffle size={18} />
              Randomize
            </Button>
          </div>
          <div className="player-list">
            {data.players.length ? (
              <>
                {unassigned.length > 0 && <p className="roster-group-label">Unassigned</p>}
                {unassigned.map((player) => (
                  <Card key={player.id} className="player-row player-row--unassigned">
                    <span>
                      <GameIcon name={player.avatar} />
                    </span>
                    <div>
                      <strong>{player.nickname}</strong>
                    </div>
                    <select
                      value=""
                      aria-label={`Assign ${player.nickname}`}
                      onChange={(e) =>
                        act(
                          `/api/admin/players/${player.id}/team`,
                          { teamId: e.target.value || null },
                          'PATCH',
                        )
                      }
                    >
                      <option value="">Choose team…</option>
                      {data.teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </Card>
                ))}
                {data.players.some((p) => p.teamId) && (
                  <p className="roster-group-label">Assigned</p>
                )}
                {data.players
                  .filter((p) => p.teamId)
                  .map((player) => (
                    <Card key={player.id} className="player-row">
                      <span>
                        <GameIcon name={player.avatar} />
                      </span>
                      <div>
                        <strong>{player.nickname}</strong>
                      </div>
                      <select
                        value={player.teamId ?? ''}
                        aria-label={`Team for ${player.nickname}`}
                        onChange={(e) =>
                          act(
                            `/api/admin/players/${player.id}/team`,
                            { teamId: e.target.value || null },
                            'PATCH',
                          )
                        }
                      >
                        <option value="">Unassigned</option>
                        {data.teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                    </Card>
                  ))}
              </>
            ) : (
              <Empty>No players have registered yet.</Empty>
            )}
          </div>
        </section>
      </div>

      {teamModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={isEditing ? 'Edit team' : 'New team'}
          onClick={() => setTeamModal(null)}
        >
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit team' : 'New team'}</h2>
              <Button variant="ghost" onClick={() => setTeamModal(null)} aria-label="Close">
                <X size={16} />
              </Button>
            </div>
            <div className="add-team-form">
              <div className="team-name-row">
                <span className="team-icon" style={teamStyle(form.color)} aria-hidden>
                  <GameIcon name={form.icon} />
                </span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Team name"
                  autoFocus
                />
              </div>
              <fieldset>
                <legend>
                  Choose an icon
                  {form.icon ? (
                    <span className="field-selection">
                      {' '}
                      · {form.icon.charAt(0).toUpperCase() + form.icon.slice(1)}
                    </span>
                  ) : null}
                </legend>
                <div className="avatar-picker team-icon-choices">
                  {teamIcons.map((item) => (
                    <button
                      type="button"
                      key={item}
                      className={form.icon === item ? 'selected' : ''}
                      onClick={() => setForm({ ...form, icon: item })}
                      aria-label={item}
                    >
                      <GameIcon name={item} />
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend>
                  Choose a color
                  <span className="field-selection"> · {teamColorName(form.color)}</span>
                </legend>
                <div className="color-picker">
                  {teamColors.map((swatch) => (
                    <button
                      type="button"
                      key={swatch.name}
                      className={form.color === swatch.value ? 'selected' : ''}
                      onClick={() => setForm({ ...form, color: swatch.value })}
                      aria-label={swatch.name}
                    >
                      <span style={{ background: swatch.value }} />
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="button-wrap">
                <Button variant="secondary" onClick={randomizeForm}>
                  <Shuffle size={18} />
                  Randomize
                </Button>
                <Button busy={busy} onClick={() => void saveTeam()}>
                  {isEditing ? 'Save changes' : 'Add team'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Prompts found in src/client/prompts when the app was built. */
const deckPrompts = loadDeck();

function PhotoDeck({ data, busy, act }: { data: Snapshot; busy: boolean; act: any }) {
  const [deal, setDeal] = useState({
    dealStart: data.game.dealStart ?? 3,
    dealSize: data.game.dealSize ?? 2,
    dealEveryMinutes: data.game.dealEveryMinutes ?? 30,
  });
  const [result, setResult] = useState('');
  const categories = [...new Set(deckPrompts.map((prompt) => prompt.category))];
  const loaded = data.tasks.filter((task) => task.deckKey).length;
  const loadedKeys = new Set(data.tasks.map((task) => task.deckKey).filter(Boolean));
  const fresh = deckPrompts.filter((prompt) => !loadedKeys.has(prompt.key)).length;
  return (
    <Card className="photo-deck">
      <div className="photo-deck-header">
        <div>
          <h2>Photo deck</h2>
          <p>
            {deckPrompts.length} prompts in {categories.length} categories
            {categories.length ? ` (${categories.join(', ')})` : ''}. {loaded} loaded into this
            game.
          </p>
        </div>
        <Button
          busy={busy}
          disabled={!deckPrompts.length}
          onClick={async () => {
            const response = await act(`/api/admin/games/${data.game.id}/deck`, {
              prompts: deckPrompts,
            });
            if (response) setResult(fresh ? `Added ${fresh} new prompts.` : 'Already up to date.');
          }}
        >
          <Plus size={16} />
          {loaded ? `Load new prompts (${fresh})` : 'Load photo deck'}
        </Button>
      </div>
      {result ? <p className="field-hint">{result}</p> : null}
      <div className="form-grid photo-deck-deal">
        <label>
          Starting hand
          <input
            type="number"
            min={0}
            max={50}
            value={deal.dealStart}
            onChange={(e) => setDeal({ ...deal, dealStart: Number(e.target.value) })}
          />
        </label>
        <label>
          Prompts per drop
          <input
            type="number"
            min={0}
            max={50}
            value={deal.dealSize}
            onChange={(e) => setDeal({ ...deal, dealSize: Number(e.target.value) })}
          />
        </label>
        <label>
          Minutes between drops
          <input
            type="number"
            min={0}
            max={1440}
            value={deal.dealEveryMinutes}
            onChange={(e) => setDeal({ ...deal, dealEveryMinutes: Number(e.target.value) })}
          />
        </label>
        <Button
          variant="secondary"
          busy={busy}
          onClick={() => act(`/api/admin/games/${data.game.id}`, deal, 'PATCH')}
        >
          Save dealing
        </Button>
      </div>
      <p className="field-hint">
        Each player starts with {deal.dealStart} prompts and gets {deal.dealSize} more every{' '}
        {deal.dealEveryMinutes} minutes, mixed across categories. Players get different prompts
        until the deck runs out, and nobody gets the same one twice. Add prompts by dropping files
        in <code>src/client/prompts/</code>.
      </p>
    </Card>
  );
}

const releaseOptions: [Release, string, string][] = [
  ['start', 'Right away', 'On the player list as soon as it is published'],
  ['scheduled', 'On a timer', 'Appears a set number of minutes after the game starts'],
  ['hidden', 'Hidden', 'Only after it is found, such as a QR code'],
];

function Tasks({
  data,
  busy,
  act,
  onShowQr,
}: {
  data: Snapshot;
  busy: boolean;
  act: any;
  onShowQr: (task: { name: string; type: string; qrToken?: string | null }) => void | Promise<void>;
}) {
  const emptyForm = {
    name: '',
    type: '' as TaskType | '',
    description: '',
    points: 100,
    status: 'draft' as TaskStatus,
    release: 'hidden' as Release,
    releaseMinutes: 30,
    fromDeck: false,
    notifyPlayers: true,
    answerMode: 'free_response' as TriviaAnswerMode,
    answerOptions: ['', ''] as string[],
    correctAnswer: '',
    firstFinisherBoost: false,
    powerup: '' as PowerupKind | '',
    powerupText: '',
    hint: '',
  };
  const [typeFilter, setTypeFilter] = useState<'all' | TaskType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | TaskStatus>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [taskModal, setTaskModal] = useState<
    | null
    | { mode: 'create'; step: 'type' | 'fields' }
    | { mode: 'edit'; id: string; step: 'type' | 'fields'; previousStatus: TaskStatus }
  >(null);
  const [form, setForm] = useState(emptyForm);
  const isEditing = taskModal?.mode === 'edit';
  const onFieldsStep = taskModal?.step === 'fields';
  const willPublish =
    form.status === 'published' &&
    (!isEditing || (taskModal?.mode === 'edit' && taskModal.previousStatus !== 'published'));

  const filteredTasks = data.tasks.filter((task) => {
    if (typeFilter !== 'all' && task.type !== typeFilter) return false;
    const status = (
      taskStatuses.includes(task.status as TaskStatus) ? task.status : 'draft'
    ) as TaskStatus;
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    const visible =
      status !== 'draft' &&
      (task.visibility === 'start' || (task.visibility === 'scheduled' && task.revealedAt));
    if (visibilityFilter === 'visible' && !visible) return false;
    if (visibilityFilter === 'hidden' && visible) return false;
    return true;
  });
  const filtersActive =
    typeFilter !== 'all' || statusFilter !== 'all' || visibilityFilter !== 'all';

  useEffect(() => {
    if (!taskModal) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setTaskModal(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [taskModal]);

  function openCreate() {
    setForm(emptyForm);
    setTaskModal({ mode: 'create', step: 'type' });
  }

  function openEdit(task: {
    id: string;
    name: string;
    type: string;
    description: string;
    points: number;
    status?: string | null;
    visibility: string;
    scheduledOffsetMinutes?: number | null;
    deckKey?: string | null;
    answerMode?: string | null;
    answerOptions?: string[] | null;
    correctAnswer?: string | null;
    firstFinisherBoost?: boolean;
    powerup?: string | null;
    powerupText?: string | null;
    hint?: string | null;
  }) {
    const mode = task.answerMode === 'multiple_choice' ? 'multiple_choice' : 'free_response';
    const options =
      task.answerOptions && task.answerOptions.length >= 2 ? [...task.answerOptions] : ['', ''];
    const status = (
      taskStatuses.includes(task.status as TaskStatus) ? task.status : 'draft'
    ) as TaskStatus;
    setForm({
      name: task.name,
      type: (taskTypes.includes(task.type as TaskType) ? task.type : 'qr') as TaskType,
      description: task.description ?? '',
      points: task.points,
      status,
      release: (task.visibility === 'start' || task.visibility === 'scheduled'
        ? task.visibility
        : 'hidden') as Release,
      releaseMinutes: task.scheduledOffsetMinutes ?? 30,
      fromDeck: Boolean(task.deckKey),
      notifyPlayers: true,
      answerMode: mode,
      answerOptions: options,
      correctAnswer: task.correctAnswer ?? '',
      firstFinisherBoost: Boolean(task.firstFinisherBoost),
      powerup: (powerupKinds.includes(task.powerup as PowerupKind) ? task.powerup : '') as
        PowerupKind | '',
      powerupText: task.powerupText ?? '',
      hint: task.hint ?? '',
    });
    setTaskModal({ mode: 'edit', id: task.id, step: 'fields', previousStatus: status });
  }

  function selectType(type: TaskType) {
    setForm((prev) => ({
      ...prev,
      type,
      points: prev.points || 100,
      answerMode: type === 'trivia' ? prev.answerMode : 'free_response',
    }));
    setTaskModal((prev) => (prev ? { ...prev, step: 'fields' } : prev));
  }

  async function saveTask() {
    if (!taskModal || !form.type || !form.name.trim()) return;
    const isTrivia = form.type === 'trivia';
    const options = form.answerOptions.map((option) => option.trim()).filter(Boolean);
    if (isTrivia && form.answerMode === 'multiple_choice') {
      if (options.length < 2) {
        alert('Add at least two multiple-choice options.');
        return;
      }
      if (!form.correctAnswer.trim() || !options.includes(form.correctAnswer.trim())) {
        markCorrectNeeded();
        return;
      }
    }
    const base = {
      name: form.name.trim(),
      type: form.type,
      description: form.description,
      points: form.points,
      status: form.status,
      visibility: form.status === 'draft' || form.fromDeck ? 'hidden' : form.release,
      scheduledOffsetMinutes:
        form.status !== 'draft' && !form.fromDeck && form.release === 'scheduled'
          ? Math.max(0, Math.round(form.releaseMinutes))
          : null,
      notifyPlayers:
        willPublish && !form.fromDeck && form.release === 'start' && form.notifyPlayers,
      adminGuidance: taskTypeMeta[form.type].title,
      requiresApproval:
        form.type === 'trivia'
          ? form.answerMode === 'free_response' && !form.correctAnswer.trim()
          : ['photo', 'manual'].includes(form.type),
      requiresTeam: form.type === 'trivia' || form.type === 'photo' || form.type === 'manual',
      oncePerTeam: true,
      retryOnReject: form.type === 'trivia' ? false : true,
      answerMode: isTrivia ? form.answerMode : 'free_response',
      answerOptions: isTrivia && form.answerMode === 'multiple_choice' ? options : null,
      correctAnswer: isTrivia ? form.correctAnswer.trim() || null : null,
      enabled: true,
      firstFinisherBoost: form.firstFinisherBoost,
      powerup: form.powerup || null,
      powerupText: form.powerup === 'dare' ? form.powerupText.trim() || null : null,
      hint: form.type === 'qr' ? form.hint.trim() || null : null,
    };
    if (taskModal.mode === 'create') {
      const ok = await act(`/api/admin/games/${data.game.id}/tasks`, {
        ...base,
        individualPoints: 0,
        firstClaimOnly: false,
        duplicatePenalty: 0,
        successMessage: DEFAULT_SUCCESS_MESSAGE,
        unavailableMessage: 'This challenge is unavailable.',
      });
      if (ok) setTaskModal(null);
      return;
    }
    const ok = await act(`/api/admin/tasks/${taskModal.id}`, base, 'PATCH');
    if (ok) setTaskModal(null);
  }

  function markCorrectNeeded() {
    alert('Select which option is the correct answer.');
  }

  return (
    <>
      <div className="section-title">
        <div>
          <h2>Tasks</h2>
          <p>Draft challenges, publish when ready, and close them when time is up.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus size={18} />
          New Task
        </Button>
      </div>
      <PhotoDeck key={data.game.id} data={data} busy={busy} act={act} />
      {data.tasks.length > 0 && (
        <div className="task-filters">
          <label>
            <span>Type</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'all' | TaskType)}
              aria-label="Filter by task type"
            >
              <option value="all">All types</option>
              {taskTypes.map((type) => (
                <option key={type} value={type}>
                  {taskTypeMeta[type].title}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | TaskStatus)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              {taskStatuses.map((status) => (
                <option key={status} value={status}>
                  {taskStatusMeta[status].title}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Visibility</span>
            <select
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value as 'all' | 'visible' | 'hidden')}
              aria-label="Filter by player visibility"
            >
              <option value="all">All visibility</option>
              <option value="visible">Visible to players</option>
              <option value="hidden">Hidden from players</option>
            </select>
          </label>
        </div>
      )}
      <div className="task-admin-list">
        {filteredTasks.length ? (
          filteredTasks.map((task) => {
            const type = (
              taskTypes.includes(task.type as TaskType) ? task.type : 'manual'
            ) as TaskType;
            const typeTitle = taskTypeMeta[type].title;
            return (
              <Card key={task.id} className="task-admin-row">
                <div className="task-type" title={typeTitle}>
                  <span className="task-type-icon" aria-label={typeTitle}>
                    <PixelIcon icon={taskTypeIcons[type]} label={typeTitle} />
                  </span>
                </div>
                <div className="task-admin-copy">
                  <div className="task-admin-title-row">
                    <h3>{task.name}</h3>
                    <span
                      className={`task-status task-status--${task.status || 'draft'}`}
                      title={
                        taskStatusMeta[
                          (taskStatuses.includes(task.status as TaskStatus)
                            ? task.status
                            : 'draft') as TaskStatus
                        ].description
                      }
                    >
                      {
                        taskStatusMeta[
                          (taskStatuses.includes(task.status as TaskStatus)
                            ? task.status
                            : 'draft') as TaskStatus
                        ].title
                      }
                    </span>
                    {task.category ? <span className="task-category">{task.category}</span> : null}
                    {task.visibility === 'scheduled' && task.status !== 'draft' ? (
                      <span className="task-release">
                        {releaseLabel(task, data.game.startedAt)}
                      </span>
                    ) : null}
                    <span
                      className={`task-visibility ${task.visibility === 'start' && task.status !== 'draft' ? 'is-visible' : 'is-hidden'}`}
                      title={
                        task.status === 'draft'
                          ? 'Drafts are never visible to players'
                          : task.visibility === 'start'
                            ? 'Visible to players'
                            : 'Hidden from players'
                      }
                      aria-label={
                        task.status === 'draft'
                          ? 'Drafts are never visible to players'
                          : task.visibility === 'start'
                            ? 'Visible to players'
                            : 'Hidden from players'
                      }
                    >
                      {task.visibility === 'start' && task.status !== 'draft' ? (
                        <Eye size={16} />
                      ) : (
                        <EyeOff size={16} />
                      )}
                    </span>
                    <span
                      className={`task-points ${task.points < 0 ? 'task-points--trap' : ''}`}
                      title={task.points < 0 ? 'Trap: scanning costs points' : undefined}
                    >
                      {task.points < 0 ? 'Trap ' : ''}
                      {task.points} pts
                    </span>
                    {POWERUPS_ENABLED && task.firstFinisherBoost ? (
                      <span
                        className="task-badge"
                        title="First team to complete this gets 2x points for 3 minutes"
                      >
                        <PixelIcon icon={boostIcon} label="First-finisher boost" />
                      </span>
                    ) : null}
                    {POWERUPS_ENABLED && task.powerup && powerupKinds.includes(task.powerup) ? (
                      <span
                        className="task-badge"
                        title={`Rewards a ${powerupMeta[task.powerup as PowerupKind].title} powerup`}
                      >
                        <PixelIcon
                          icon={powerupIcons[task.powerup as PowerupKind]}
                          label={`${powerupMeta[task.powerup as PowerupKind].title} powerup`}
                        />
                      </span>
                    ) : null}
                  </div>
                  {task.description ? <p>{task.description}</p> : null}
                  {referenceImageUrl(task.referenceImage) ? (
                    <img
                      className="task-reference task-reference--small"
                      src={referenceImageUrl(task.referenceImage)}
                      alt={`Reference for ${task.name}`}
                      loading="lazy"
                    />
                  ) : null}
                </div>
                <div className="task-actions">
                  {task.qrToken && (
                    <Button variant="ghost" onClick={() => void onShowQr(task)}>
                      <QrCode size={16} />
                      QR
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => openEdit(task)}>
                    <Pencil size={16} />
                    Edit
                  </Button>
                </div>
              </Card>
            );
          })
        ) : (
          <Empty>
            {filtersActive
              ? 'No tasks match these filters.'
              : 'No tasks yet. Create one with New Task.'}
          </Empty>
        )}
      </div>

      {taskModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={isEditing ? 'Edit task' : 'New task'}
          onClick={() => setTaskModal(null)}
        >
          <div
            className={`modal-panel ${onFieldsStep ? '' : 'modal-panel--wide'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>
                  {!onFieldsStep ? 'Choose a task type' : isEditing ? 'Edit task' : 'New task'}
                </h2>
                {onFieldsStep && form.type ? (
                  <p className="modal-subtitle">
                    {taskTypeMeta[form.type].title}
                    <button
                      type="button"
                      className="text-button"
                      onClick={() =>
                        setTaskModal((prev) => (prev ? { ...prev, step: 'type' } : prev))
                      }
                    >
                      Change
                    </button>
                  </p>
                ) : (
                  <p className="modal-subtitle">Pick how players will complete this challenge.</p>
                )}
              </div>
              <Button variant="ghost" onClick={() => setTaskModal(null)} aria-label="Close">
                <X size={16} />
              </Button>
            </div>

            {!onFieldsStep ? (
              <div className="task-type-grid">
                {taskTypes.map((type) => {
                  const meta = taskTypeMeta[type];
                  return (
                    <button
                      type="button"
                      key={type}
                      className={`task-type-tile ${form.type === type ? 'selected' : ''}`}
                      onClick={() => selectType(type)}
                    >
                      <span className="task-type-tile-icon" aria-hidden>
                        <PixelIcon icon={taskTypeIcons[type]} />
                      </span>
                      <strong>{meta.title}</strong>
                      <span>{meta.description}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="add-team-form">
                <div className="form-grid">
                  <label className="span-2">
                    Name
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Task name"
                      autoFocus
                    />
                  </label>
                  <label className="span-2">
                    {form.type === 'trivia' ? 'Question' : 'Player instructions'}
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder={
                        form.type === 'trivia'
                          ? 'What question should players answer?'
                          : 'What should players do?'
                      }
                      rows={3}
                    />
                  </label>
                  {form.type === 'trivia' && (
                    <>
                      <fieldset className="span-2">
                        <legend>Answer type</legend>
                        <div className="answer-mode-picker">
                          {(
                            [
                              ['free_response', 'Free Response', 'Players type an answer'],
                              [
                                'multiple_choice',
                                'Multiple Choice',
                                'Players pick from options you define',
                              ],
                            ] as const
                          ).map(([mode, title, description]) => (
                            <button
                              type="button"
                              key={mode}
                              className={form.answerMode === mode ? 'selected' : ''}
                              onClick={() =>
                                setForm({
                                  ...form,
                                  answerMode: mode,
                                  correctAnswer:
                                    mode === 'multiple_choice' &&
                                    !form.answerOptions.includes(form.correctAnswer)
                                      ? ''
                                      : form.correctAnswer,
                                })
                              }
                            >
                              <strong>{title}</strong>
                              <span>{description}</span>
                            </button>
                          ))}
                        </div>
                      </fieldset>
                      {form.answerMode === 'free_response' ? (
                        <label className="span-2">
                          Correct answer
                          <input
                            value={form.correctAnswer}
                            onChange={(e) => setForm({ ...form, correctAnswer: e.target.value })}
                            placeholder="Optional — leave blank to review manually"
                          />
                        </label>
                      ) : (
                        <div className="span-2 trivia-options">
                          <span className="trivia-options-label">Options</span>
                          {form.answerOptions.map((option, index) => (
                            <div className="trivia-option-row" key={index}>
                              <input
                                type="radio"
                                name="correct-option"
                                checked={form.correctAnswer === option && option.trim() !== ''}
                                disabled={!option.trim()}
                                onChange={() => setForm({ ...form, correctAnswer: option.trim() })}
                                aria-label={`Mark option ${index + 1} as correct`}
                              />
                              <input
                                value={option}
                                onChange={(e) => {
                                  const next = [...form.answerOptions];
                                  next[index] = e.target.value;
                                  setForm({
                                    ...form,
                                    answerOptions: next,
                                    correctAnswer:
                                      form.correctAnswer === option
                                        ? e.target.value.trim()
                                        : form.correctAnswer,
                                  });
                                }}
                                placeholder={`Option ${index + 1}`}
                              />
                              {form.answerOptions.length > 2 && (
                                <Button
                                  variant="ghost"
                                  onClick={() => {
                                    const next = form.answerOptions.filter((_, i) => i !== index);
                                    setForm({
                                      ...form,
                                      answerOptions: next,
                                      correctAnswer:
                                        form.correctAnswer === option ? '' : form.correctAnswer,
                                    });
                                  }}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          ))}
                          {form.answerOptions.length < 8 && (
                            <Button
                              variant="secondary"
                              onClick={() =>
                                setForm({ ...form, answerOptions: [...form.answerOptions, ''] })
                              }
                            >
                              <Plus size={16} />
                              Add option
                            </Button>
                          )}
                          <small className="field-hint">
                            Select the radio next to the correct option. Teams submit once and are
                            scored immediately.
                          </small>
                        </div>
                      )}
                    </>
                  )}
                  <label>
                    Points
                    <input
                      type="number"
                      value={form.points}
                      onChange={(e) => setForm({ ...form, points: Number(e.target.value) })}
                    />
                    <span className="field-hint">
                      {form.points < 0
                        ? form.release !== 'hidden'
                          ? 'Negative points make this a trap, but players will see it, so they know it costs points. Set “When players see it” to Hidden to keep it secret.'
                          : 'Negative points make this a trap: teams lose these points when they complete it. Players only see it after it is triggered.'
                        : 'Use a negative number for a trap, such as a decoy QR code that costs points.'}
                    </span>
                  </label>
                  {POWERUPS_ENABLED && (
                    <>
                      <label className="span-2 toggle">
                        <input
                          type="checkbox"
                          checked={form.firstFinisherBoost}
                          onChange={(e) =>
                            setForm({ ...form, firstFinisherBoost: e.target.checked })
                          }
                        />
                        First-finisher boost: the first team to complete this gets 2x points for 3
                        minutes
                      </label>
                      <label>
                        Powerup reward
                        <select
                          value={form.powerup}
                          onChange={(e) =>
                            setForm({ ...form, powerup: e.target.value as PowerupKind | '' })
                          }
                        >
                          <option value="">None</option>
                          {powerupKinds.map((kind) => (
                            <option key={kind} value={kind}>
                              {powerupMeta[kind].title}
                            </option>
                          ))}
                        </select>
                        <span className="field-hint">
                          {form.powerup
                            ? `Completing this gives the team a ${powerupMeta[form.powerup].title} powerup: ${powerupMeta[form.powerup].description}`
                            : 'Completing this task can also give the team a powerup to use later.'}
                        </span>
                      </label>
                      {form.powerup === 'dare' ? (
                        <label>
                          Dare text
                          <input
                            value={form.powerupText}
                            maxLength={200}
                            placeholder={DEFAULT_DARE}
                            onChange={(e) => setForm({ ...form, powerupText: e.target.value })}
                          />
                          <span className="field-hint">
                            Shown to the team that receives the dare. Keep a non-drinking option in
                            mind.
                          </span>
                        </label>
                      ) : null}
                      {form.type === 'qr' ? (
                        <label className="span-2">
                          Hint
                          <textarea
                            value={form.hint}
                            maxLength={500}
                            placeholder="Where is this code hidden? e.g. Look under something that glows."
                            onChange={(e) => setForm({ ...form, hint: e.target.value })}
                          />
                          <span className="field-hint">
                            Shown to a team that uses a Hint powerup before finding this code.
                          </span>
                        </label>
                      ) : null}
                    </>
                  )}
                  <fieldset className="span-2">
                    <legend>Status</legend>
                    <div className="answer-mode-picker status-picker">
                      {taskStatuses.map((status) => (
                        <button
                          type="button"
                          key={status}
                          className={form.status === status ? 'selected' : ''}
                          onClick={() =>
                            setForm({
                              ...form,
                              status,
                              release: status === 'draft' ? 'hidden' : form.release,
                              notifyPlayers:
                                status === 'draft' || form.release !== 'start'
                                  ? false
                                  : form.notifyPlayers,
                            })
                          }
                        >
                          <strong>{taskStatusMeta[status].title}</strong>
                          <span>{taskStatusMeta[status].description}</span>
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  {form.status !== 'draft' && form.fromDeck && (
                    <p className="span-2 field-hint">
                      From the photo deck: players get this when it is dealt into their hand.
                    </p>
                  )}
                  {form.status !== 'draft' && !form.fromDeck && (
                    <>
                      <fieldset className="span-2">
                        <legend>When players see it</legend>
                        <div className="answer-mode-picker status-picker">
                          {releaseOptions.map(([value, title, description]) => (
                            <button
                              type="button"
                              key={value}
                              className={form.release === value ? 'selected' : ''}
                              onClick={() =>
                                setForm({
                                  ...form,
                                  release: value,
                                  notifyPlayers: value === 'start' ? form.notifyPlayers : false,
                                })
                              }
                            >
                              <strong>{title}</strong>
                              <span>{description}</span>
                            </button>
                          ))}
                        </div>
                      </fieldset>
                      {form.release === 'scheduled' && (
                        <label>
                          Minutes after the game starts
                          <input
                            type="number"
                            min={0}
                            max={10080}
                            value={form.releaseMinutes}
                            onChange={(e) =>
                              setForm({ ...form, releaseMinutes: Number(e.target.value) })
                            }
                          />
                          <span className="field-hint">
                            {data.game.startedAt
                              ? `About ${releaseTime(data.game.startedAt, form.releaseMinutes)!.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. `
                              : ''}
                            Players and the party display get a “New challenge” notice when it
                            appears.
                          </span>
                        </label>
                      )}
                      <p className="span-2 field-hint">
                        {form.status === 'closed'
                          ? 'Closed tasks can stay on the player list, but they no longer accept submissions.'
                          : 'Completed tasks always stay on the player’s list.'}
                      </p>
                    </>
                  )}
                  {willPublish && !form.fromDeck && form.release === 'start' && (
                    <label className="span-2 toggle">
                      <input
                        type="checkbox"
                        checked={form.notifyPlayers}
                        onChange={(e) => setForm({ ...form, notifyPlayers: e.target.checked })}
                      />
                      Send notification when publishing
                    </label>
                  )}
                </div>
                <div className="button-wrap">
                  {isEditing && (
                    <Button
                      variant="danger"
                      busy={busy}
                      onClick={() => {
                        if (
                          !taskModal ||
                          taskModal.mode !== 'edit' ||
                          !confirm(
                            `Delete “${form.name.trim() || 'this task'}”? All points awarded for this task will be removed.`,
                          )
                        )
                          return;
                        void (async () => {
                          const ok = await act(
                            `/api/admin/tasks/${taskModal.id}`,
                            undefined,
                            'DELETE',
                          );
                          if (ok) setTaskModal(null);
                        })();
                      }}
                    >
                      <Trash2 size={16} />
                      Delete
                    </Button>
                  )}
                  {!isEditing && (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setTaskModal((prev) => (prev ? { ...prev, step: 'type' } : prev))
                      }
                    >
                      Back
                    </Button>
                  )}
                  <Button busy={busy} disabled={!form.name.trim()} onClick={() => void saveTask()}>
                    {isEditing ? 'Save changes' : 'Add task'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Submissions({ data, busy, act }: { data: Snapshot; busy: boolean; act: any }) {
  const [photoId, setPhotoId] = useState<string | null>(null);
  const pending = data.submissions.filter((s) => s.status === 'pending');
  const decided = data.submissions.filter((s) => s.status !== 'pending');
  const photoItem = photoId ? data.submissions.find((s) => s.id === photoId) : null;
  const mediaUrl = (mediaId: string) => `/api/admin/games/${data.game.id}/media/${mediaId}`;

  useEffect(() => {
    if (!photoId) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setPhotoId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoId]);

  function row(s: any) {
    return (
      <Submission
        key={s.id}
        item={s}
        busy={busy}
        act={act}
        mediaUrl={mediaUrl}
        onOpenPhoto={() => setPhotoId(s.id)}
      />
    );
  }

  return (
    <>
      <h2>Pending Submissions</h2>
      <div className="submission-list">
        {pending.length ? pending.map(row) : <Empty>Nothing is waiting for review.</Empty>}
      </div>
      <h2>Reviewed</h2>
      <div className="submission-list">{decided.slice(0, 30).map(row)}</div>
      {photoItem?.mediaId && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Submission photo"
          onClick={() => setPhotoId(null)}
        >
          <div
            className="modal-panel modal-panel--wide submission-photo-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>{photoItem.taskName}</h2>
                <p className="modal-subtitle">
                  {photoItem.teamName ? `${photoItem.teamName} · ` : ''}
                  {photoItem.nickname} · {relative(photoItem.createdAt)}
                </p>
              </div>
              <Button variant="ghost" onClick={() => setPhotoId(null)} aria-label="Close">
                <X size={16} />
              </Button>
            </div>
            <AdminPhoto
              className="submission-photo-full"
              src={mediaUrl(photoItem.mediaId)}
              alt={`Photo submitted for ${photoItem.taskName}`}
            />
            <div className="submission-photo-footer">
              <span className={`pill pill--${photoItem.status}`}>{photoItem.status}</span>
              {photoItem.status === 'pending' && (
                <ReviewButtons
                  item={photoItem}
                  busy={busy}
                  act={act}
                  onDone={() => setPhotoId(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
function Submission({
  item,
  busy,
  act,
  mediaUrl,
  onOpenPhoto,
}: {
  item: any;
  busy: boolean;
  act: any;
  mediaUrl: (mediaId: string) => string;
  onOpenPhoto: () => void;
}) {
  return (
    <Card className="submission-row">
      {item.mediaId && (
        <button
          type="button"
          className="submission-thumb"
          onClick={onOpenPhoto}
          aria-label={`View photo for ${item.taskName}`}
        >
          <AdminPhoto src={mediaUrl(item.mediaId)} alt="" />
        </button>
      )}
      <div>
        <span className={`pill pill--${item.status}`}>{item.status}</span>
        <h3>{item.taskName}</h3>
        <p>
          {item.teamName ?? item.nickname} · {item.answerText}
        </p>
        <small>{relative(item.createdAt)}</small>
      </div>
      {item.status === 'pending' && <ReviewButtons item={item} busy={busy} act={act} />}
    </Card>
  );
}
function AdminPhoto({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setObjectUrl(null);
    setFailed(false);
    apiBlob(src)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);

  if (failed)
    return <span className={`admin-photo-missing ${className ?? ''}`}>Photo unavailable</span>;
  if (!objectUrl) return <span className={`admin-photo-loading ${className ?? ''}`} aria-hidden />;
  return <img className={className} src={objectUrl} alt={alt} />;
}
function ReviewButtons({
  item,
  busy,
  act,
  onDone,
}: {
  item: any;
  busy: boolean;
  act: any;
  onDone?: () => void;
}) {
  async function review(body: Record<string, unknown>) {
    const ok = await act(`/api/admin/submissions/${item.id}/review`, body);
    if (ok) onDone?.();
  }
  return (
    <div className="review-buttons">
      <Button
        variant="success"
        busy={busy}
        onClick={() =>
          void review({ decision: 'approved', displayApproved: Boolean(item.mediaId) })
        }
      >
        <Check size={16} />
        Approve
      </Button>
      <Button
        variant="danger"
        busy={busy}
        onClick={() =>
          void review({
            decision: 'rejected',
            reason: 'Please try again',
            displayApproved: false,
          })
        }
      >
        <X size={16} />
        Reject
      </Button>
    </div>
  );
}

function AnnouncementModal({
  data,
  busy,
  act,
  onClose,
}: {
  data: Snapshot;
  busy: boolean;
  act: any;
  onClose: () => void;
}) {
  const teamIds = data.teams.map((team) => team.id as string);
  const [message, setMessage] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(15);
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(true);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(teamIds);
  const [teamsOpen, setTeamsOpen] = useState(false);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setSelectedTeamIds((prev) => {
      const next = teamIds.filter((id) => prev.includes(id));
      const missing = teamIds.filter((id) => !prev.includes(id));
      return [...next, ...missing];
    });
  }, [teamIds.join('|')]);

  const allTeamsSelected =
    teamIds.length > 0 && teamIds.every((id) => selectedTeamIds.includes(id));
  const teamSummary = !teamIds.length
    ? 'No teams yet'
    : allTeamsSelected
      ? 'All teams'
      : selectedTeamIds.length === 0
        ? 'No teams selected'
        : `${selectedTeamIds.length} of ${teamIds.length} teams`;

  async function sendAnnouncement() {
    if (!message.trim() || selectedTeamIds.length === 0) return;
    const ok = await act(`/api/admin/games/${data.game.id}/announcements`, {
      message: message.trim(),
      durationSeconds,
      showOnLeaderboard,
      teamIds: allTeamsSelected ? null : selectedTeamIds,
    });
    if (ok) onClose();
  }

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Send announcement"
      onClick={onClose}
    >
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Send Announcement</h2>
            <p className="modal-subtitle">Message players and optionally the party display.</p>
          </div>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            <X size={16} />
          </Button>
        </div>
        <div className="stack announcement-form">
          <label>
            Message
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What should players see?"
              rows={4}
              autoFocus
            />
          </label>
          <label>
            Display duration (seconds)
            <input
              type="number"
              min={5}
              max={600}
              value={durationSeconds}
              onChange={(e) => setDurationSeconds(Math.max(5, Number(e.target.value) || 15))}
            />
          </label>
          <fieldset className="send-to-fieldset">
            <legend>Send To</legend>
            <label className="toggle">
              <input
                type="checkbox"
                checked={showOnLeaderboard}
                onChange={(e) => setShowOnLeaderboard(e.target.checked)}
              />
              Leaderboard
            </label>
            <div className={`team-multiselect ${teamsOpen ? 'is-open' : ''}`}>
              <span className="team-multiselect-label">Teams</span>
              <button
                type="button"
                className="team-multiselect-trigger"
                onClick={() => setTeamsOpen((open) => !open)}
                aria-expanded={teamsOpen}
                disabled={!teamIds.length}
              >
                {teamSummary}
              </button>
              {teamsOpen && teamIds.length > 0 && (
                <div className="team-multiselect-menu" role="listbox" aria-multiselectable="true">
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={allTeamsSelected}
                      onChange={(e) => setSelectedTeamIds(e.target.checked ? [...teamIds] : [])}
                    />
                    All teams
                  </label>
                  {data.teams.map((team) => {
                    const checked = selectedTeamIds.includes(team.id);
                    return (
                      <label className="toggle" key={team.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setSelectedTeamIds((prev) =>
                              e.target.checked
                                ? [...prev, team.id]
                                : prev.filter((id) => id !== team.id),
                            )
                          }
                        />
                        {team.name}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="field-hint">
              Players on selected teams see this on their phones
              {showOnLeaderboard ? ', and it appears on the party leaderboard' : ''}, for{' '}
              {durationSeconds}s.
            </p>
          </fieldset>
          <div className="button-wrap">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              busy={busy}
              disabled={!message.trim() || selectedTeamIds.length === 0}
              onClick={() => void sendAnnouncement()}
            >
              <Megaphone size={18} />
              Send Announcement
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function relative(value: string) {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value + 'Z').getTime()) / 1000));
  return seconds < 60
    ? 'just now'
    : seconds < 3600
      ? `${Math.floor(seconds / 60)}m ago`
      : `${Math.floor(seconds / 3600)}h ago`;
}
